import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createCheckout } from '../api';
import { createMetaEventId, hasMarketingConsent, metaTrack } from '../analytics/metaPixel';

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem('wearhouse60_cart_v1');
      const saved = raw ? JSON.parse(raw) : [];
      return Array.isArray(saved) ? saved.filter(item => item.productId === 'wearhouse60-jean' && ['1', '2', '3'].includes(item.size) && Number.isInteger(item.qty) && item.qty > 0 && Number.isFinite(item.price) && item.maxQty > 0) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [promoCode, setPromoCode] = useState(() => localStorage.getItem('wearhouse60_promo') || '');

  useEffect(() => {
    localStorage.setItem('wearhouse60_cart_v1', JSON.stringify(items));
  }, [items]);

  // Promo code carried by abandoned-cart reminder emails (?promo=RETOURXXXXXX)
  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('promo');
    if (fromUrl) {
      const code = fromUrl.trim().toUpperCase();
      setPromoCode(code);
      localStorage.setItem('wearhouse60_promo', code);
    }
  }, []);

  const clearPromo = useCallback(() => {
    setPromoCode('');
    localStorage.removeItem('wearhouse60_promo');
  }, []);

  const applyPromo = useCallback((code) => {
    const clean = (code || '').trim().toUpperCase();
    if (!clean) return;
    setPromoCode(clean);
    localStorage.setItem('wearhouse60_promo', clean);
  }, []);

  // item: { sku, productId, variantId, variantName, size, qty, price, name, image }
  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = `${item.sku}-${item.size}`;
      const existing = prev.find((p) => p.key === key);
      if (existing) {
        return prev.map((p) => (p.key === key ? { ...item, key, qty: Math.min(item.maxQty, p.qty + item.qty) } : p));
      }
      return [...prev, { ...item, key }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((p) => p.key !== key));
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) => prev.map((p) => (p.key === key ? { ...p, qty: Math.max(1, Math.min(p.maxQty, qty)) } : p)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const checkout = useCallback(async (opts = {}) => {
    if (items.length === 0) return;
    setCheckingOut(true);
    try {
      const marketingConsent = hasMarketingConsent();
      const metaEventId = marketingConsent ? createMetaEventId() : undefined;
      const payload = {
        items: items.map((i) => ({
          lookup_key: i.sku,
          product_id: i.productId,
          variant_id: i.variantId || '',
          size: String(i.size),
          quantity: i.qty,
        })),
        origin_url: window.location.origin,
        email: opts.email || undefined,
        subscribe: !!opts.subscribe,
        promo_code: promoCode || undefined,
        marketing_consent: marketingConsent,
        meta_event_id: metaEventId,
        event_source_path: window.location.pathname,
      };
      const data = await createCheckout(payload);
      metaTrack('InitiateCheckout', {
        content_type: 'product',
        content_ids: items.map((item) => item.productId),
        value: items.reduce((total, item) => total + item.price * item.qty, 0),
        currency: 'EUR',
        num_items: items.reduce((total, item) => total + item.qty, 0),
      }, metaEventId);
      window.location.href = data.checkout_url;
    } catch (e) {
      setCheckingOut(false);
      throw e;
    }
  }, [items, promoCode]);

  const count = items.reduce((n, p) => n + p.qty, 0);
  const subtotal = items.reduce((n, p) => n + p.qty * Math.round(p.price * 100), 0) / 100;

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clearCart, checkout, checkingOut, count, subtotal, open, setOpen, promoCode, clearPromo, applyPromo }}
    >
      {children}
    </CartContext.Provider>
  );
};
